import { useEffect, useRef, useState } from "react";
import { MessageSquareHeart, X, Send, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";

const STORAGE_KEY = "dulceria_chat_session";
const SUGGESTIONS = [
    "¿Qué es el chamoy?",
    "¿Qué es el pulparindo?",
    "¿Cómo preparo una michelada?",
    "Recomiéndame algo bien picosito",
];

function getSessionId() {
    let s = localStorage.getItem(STORAGE_KEY);
    if (!s) {
        s = "sess-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        localStorage.setItem(STORAGE_KEY, s);
    }
    return s;
}

export default function AIChat() {
    const [open, setOpen] = useState(false);
    const { open: cartOpen } = useCart();
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "¡Hola! Soy Lupita 🌶️ Pregúntame por dulces, salsas o recetas mexicanas. ¿En qué te ayudo hoy?",
        },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const sessionId = useRef(getSessionId());
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || sending) return;
        setMessages((prev) => [...prev, { role: "user", content: msg }]);
        setInput("");
        setSending(true);
        try {
            const { data } = await api.post("/chat", {
                session_id: sessionId.current,
                message: msg,
            });
            setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "Uy, tuve un problemita 😅. Inténtalo de nuevo o escríbenos por WhatsApp +56 9 9809 1369.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {!open && !cartOpen && (
                <button
                    data-testid="ai-chat-toggle-btn"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 bg-mexred hover:bg-mexred_hover text-cocoa px-4 sm:px-5 py-2.5 sm:py-3 rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2 group"
                >
                    <span className="relative flex w-2.5 h-2.5">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-mexyellow opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-mexyellow" />
                    </span>
                    <MessageSquareHeart className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.2} />
                    <span className="font-semibold text-xs sm:text-sm">¿Te ayudo?</span>
                </button>
            )}

            {open && (
                <div
                    data-testid="ai-chat-window"
                    className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border-2 border-mexred/20 animate-fade-up"
                >
                    <header className="bg-gradient-to-r from-mexred to-marigold text-cocoa p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center">
                                <Sparkles className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="font-display font-semibold leading-tight">Lupita · La Dulcería</div>
                                <div className="text-xs text-white/80">Asistente IA · en línea</div>
                            </div>
                        </div>
                        <button
                            data-testid="ai-chat-close-btn"
                            onClick={() => setOpen(false)}
                            className="p-1.5 rounded-full hover:bg-white/20"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </header>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream/40">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                                        m.role === "user"
                                            ? "bg-mexred text-cocoa rounded-br-md"
                                            : "bg-white border border-border text-cocoa rounded-bl-md"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div className="flex justify-start">
                                <div className="bg-white border border-border px-4 py-2.5 rounded-2xl rounded-bl-md">
                                    <span className="flex gap-1">
                                        <span className="w-2 h-2 bg-mexred rounded-full animate-bounce" />
                                        <span className="w-2 h-2 bg-mexred rounded-full animate-bounce [animation-delay:0.15s]" />
                                        <span className="w-2 h-2 bg-mexred rounded-full animate-bounce [animation-delay:0.3s]" />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {messages.length <= 2 && (
                        <div className="px-3 pb-2 flex flex-wrap gap-2">
                            {SUGGESTIONS.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`chat-suggest-${s}`}
                                    onClick={() => send(s)}
                                    className="text-xs bg-mexyellow/40 hover:bg-mexyellow text-cocoa px-3 py-1.5 rounded-full font-medium transition-colors"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                        className="border-t border-border p-3 flex items-center gap-2 bg-white"
                    >
                        <input
                            data-testid="ai-chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Pregúntame algo…"
                            className="flex-1 px-4 py-2.5 rounded-full bg-muted/60 border border-transparent focus:bg-white focus:border-mexred outline-none text-sm"
                        />
                        <button
                            data-testid="ai-chat-send-btn"
                            type="submit"
                            disabled={sending || !input.trim()}
                            className="bg-mexred hover:bg-mexred_hover disabled:bg-muted text-cocoa p-2.5 rounded-full transition-all"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
